import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { Heading } from "react-aria-components";
import z from "zod";

import { Button } from "@/components/base/button";
import {
  Disclosure,
  DisclosureHeader,
  DisclosurePanel,
} from "@/components/base/disclosure";
import { useAppForm } from "@/components/base/form";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import { Radio } from "@/components/base/radio-group";
import type { Division, TournamentDivision } from "@/db/schema";
import { getTournamentDivisionDisplay } from "@/hooks/tournament";
import { useTRPC } from "@/integrations/trpc/react";
import { formatScoreSettings, POOL_MATCH_DEFAULTS } from "@/lib/pool-matches";

import { usePoolsQueryOptions, useTeamsQueryOptions } from "../context";

export type CreatePoolMatchesFormProps = {
  tournamentId: number;
  division: TournamentDivision & { division: Division };
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

type MatchKind = "set" | "match";

type PoolSizeScoring = {
  poolSize: number;
  matchKind: MatchKind;
  sets: { winScore: number; switchScore: number }[];
};

function defaultScoringForSize(poolSize: number): PoolSizeScoring {
  const defaults = POOL_MATCH_DEFAULTS[poolSize];
  if (!defaults) {
    return {
      poolSize,
      matchKind: "set",
      sets: [{ winScore: 21, switchScore: 7 }],
    };
  }
  return {
    poolSize,
    matchKind: defaults.sets.length > 1 ? "match" : "set",
    sets: defaults.sets.map((s) => ({
      winScore: s.winScore,
      switchScore: s.switchScore ?? 7,
    })),
  };
}

export function CreatePoolMatchesForm({
  tournamentId,
  division,
  onOpenChange,
  ...props
}: CreatePoolMatchesFormProps) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const teamsQueryOptions = useTeamsQueryOptions();
  const poolsQueryOptions = usePoolsQueryOptions();

  const { data: pools } = useQuery(poolsQueryOptions);

  const hasExistingMatches = useMemo(
    () => pools?.some((p) => p.matches.length > 0) ?? false,
    [pools],
  );

  const poolSizes = useMemo(() => {
    if (!pools) return [];
    const sizes = new Set(pools.map((p) => p.teams.length));
    return [...sizes].sort((a, b) => a - b);
  }, [pools]);

  const { mutate, error } = useMutation({
    ...trpc.tournaments.createPoolMatches.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(teamsQueryOptions);
      queryClient.invalidateQueries(poolsQueryOptions);

      onOpenChange(false);
    },
  });

  const schema = z.object({
    overwrite: z.boolean(),
    customScoring: z.boolean(),
    scoring: z.array(
      z.object({
        poolSize: z.number(),
        matchKind: z.enum<MatchKind[]>(["set", "match"]),
        sets: z.array(
          z.object({
            winScore: z.number(),
            switchScore: z.number(),
          }),
        ),
      }),
    ),
  });

  const form = useAppForm({
    defaultValues: {
      overwrite: false,
      customScoring: false,
      scoring: poolSizes.map(defaultScoringForSize),
    } as z.infer<typeof schema>,
    validators: {
      onMount: schema,
      onChange: schema,
    },
    listeners: {
      onChange: ({ fieldApi, formApi }) => {
        const match = fieldApi.name.match(/^scoring\[(\d+)]\.matchKind$/);
        if (match) {
          const idx = Number(match[1]);
          const sets =
            fieldApi.state.value === "set"
              ? [{ winScore: 28, switchScore: 7 }]
              : [
                  { winScore: 21, switchScore: 7 },
                  { winScore: 21, switchScore: 7 },
                  { winScore: 15, switchScore: 5 },
                ];
          formApi.setFieldValue(`scoring[${idx}].sets`, sets);
        }
      },
    },
    onSubmit: ({ value: { overwrite, customScoring, scoring } }) => {
      mutate({
        id: division.id,
        tournamentId,
        overwrite,
        scoring: customScoring
          ? scoring.map((s) => ({
              poolSize: s.poolSize,
              sets: s.sets,
            }))
          : undefined,
      });
    },
  });

  // Sync scoring defaults when pool sizes change (e.g., pools data loads after mount)
  const poolSizesKey = poolSizes.join(",");
  // oxlint-disable exhaustive-deps -- poolSizesKey serializes poolSizes; form is stable
  useEffect(() => {
    if (poolSizes.length > 0) {
      form.setFieldValue("scoring", poolSizes.map(defaultScoringForSize));
    }
  }, [poolSizesKey]);
  // oxlint-enable exhaustive-deps

  return (
    <Modal {...props} onOpenChange={onOpenChange}>
      <div className="relative flex flex-col space-y-4 p-3">
        <Heading className={title({ size: "sm" })} slot="title">
          Create Pool Matches
        </Heading>

        <p className="mb-6 text-sm text-gray-700">
          Create pool matches for the{" "}
          <span className="font-bold italic">
            {getTournamentDivisionDisplay(division)}
          </span>{" "}
          division. If you want to regenerate the schedule, select{" "}
          <span className="font-semibold italic">Overwrite existing</span>.
          In-progress and completed matches will be preserved.
        </p>

        {poolSizes.length > 0 && (
          <div className="space-y-1">
            {poolSizes.map((size) => {
              const count = pools?.filter(
                (p) => p.teams.length === size,
              ).length;
              const defaults = defaultScoringForSize(size);
              return (
                <p key={size} className="text-sm text-gray-600">
                  <span className="font-medium">
                    {count}× {size}-team pool{count !== 1 ? "s" : ""}
                  </span>
                  {" — "}
                  {formatScoreSettings(defaults.sets)}
                </p>
              );
            })}
          </div>
        )}

        <form
          className="flex flex-col space-y-6"
          onSubmit={(e) => {
            e.preventDefault();

            form.handleSubmit();
          }}
        >
          {error && (
            <form.AppForm>
              <form.Alert
                title={"Unable to create pool matches"}
                description={error.message}
              />
            </form.AppForm>
          )}

          <form.AppField
            name="customScoring"
            children={(field) => (
              <field.Checkbox
                label="Custom scoring"
                description="Override the default scoring format. You can set different formats for each pool size."
                field={field}
              />
            )}
          />

          <form.Subscribe selector={(state) => state.values.customScoring}>
            {(customScoring) =>
              customScoring ? (
                <div className="flex flex-col gap-6 border-l-2 border-gray-200 pl-4">
                  {poolSizes.map((size, idx) => (
                    <div key={size} className="flex flex-col gap-3">
                      <p className="text-sm font-semibold text-gray-700">
                        {size}-team pools
                      </p>

                      <form.AppField
                        name={`scoring[${idx}].matchKind`}
                        children={(field) => (
                          <field.RadioGroup
                            label="Match Style"
                            orientation="horizontal"
                            field={field}
                            mode="string"
                          >
                            <Radio value="set">Single Set</Radio>
                            <Radio value="match">Best of 3</Radio>
                          </field.RadioGroup>
                        )}
                      />

                      <Disclosure card={false}>
                        <DisclosureHeader card={false} size="sm">
                          Score Options
                        </DisclosureHeader>
                        <DisclosurePanel card={false}>
                          <form.AppField
                            name={`scoring[${idx}].sets`}
                            mode="array"
                          >
                            {(field) => (
                              <div className="flex flex-col space-y-2">
                                {field.state.value.map(
                                  (
                                    _set: {
                                      winScore: number;
                                      switchScore: number;
                                    },
                                    i: number,
                                  ) => {
                                    const setKey = `${size}-set-${i + 1}`;
                                    return (
                                      <div
                                        className="grid grid-cols-6 gap-3"
                                        key={setKey}
                                      >
                                        <form.AppField
                                          name={`scoring[${idx}].sets[${i}].winScore`}
                                        >
                                          {(subField) => (
                                            <subField.Number
                                              className="col-span-3"
                                              field={subField}
                                              label={
                                                i === 0 ? "Win Score" : null
                                              }
                                            />
                                          )}
                                        </form.AppField>
                                        <form.AppField
                                          name={`scoring[${idx}].sets[${i}].switchScore`}
                                        >
                                          {(subField) => (
                                            <subField.Number
                                              className="col-span-3"
                                              field={subField}
                                              label={
                                                i === 0 ? "Switch Every" : null
                                              }
                                            />
                                          )}
                                        </form.AppField>
                                      </div>
                                    );
                                  },
                                )}
                              </div>
                            )}
                          </form.AppField>
                        </DisclosurePanel>
                      </Disclosure>

                      {idx < poolSizes.length - 1 && (
                        <hr className="border-gray-200" />
                      )}
                    </div>
                  ))}
                </div>
              ) : null
            }
          </form.Subscribe>

          <form.AppField
            name="overwrite"
            children={(field) => (
              <field.Checkbox label="Overwrite existing" field={field} />
            )}
          />

          <form.Subscribe selector={(state) => state.values.overwrite}>
            {(overwrite) =>
              hasExistingMatches && !overwrite ? (
                <form.AppForm>
                  <form.Alert
                    color="warning"
                    title="Existing matches detected"
                    description="This division already has pool matches. If you are sure you want to recreate them, check 'Overwrite existing'. In-progress and completed matches will be preserved."
                  />
                </form.AppForm>
              ) : null
            }
          </form.Subscribe>

          <form.AppForm>
            <form.Footer>
              <Button onPress={() => onOpenChange(false)}>Cancel</Button>

              <form.Subscribe selector={(state) => state.values.overwrite}>
                {(overwrite) => (
                  <form.SubmitButton
                    requireChange={false}
                    isDisabled={hasExistingMatches && !overwrite}
                  >
                    Create
                  </form.SubmitButton>
                )}
              </form.Subscribe>
            </form.Footer>
          </form.AppForm>
        </form>
      </div>
    </Modal>
  );
}
